import { useEffect, type FC, type ReactNode } from 'react'
import { useRoutes } from 'react-router-dom'
import { ErrorBoundary } from 'react-error-boundary'
import { Helmet } from 'react-helmet-async'
import { Provider as ReduxProvider } from 'react-redux'
import { ThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'
import useMediaQuery from '@mui/material/useMediaQuery'
import { RTL } from './components/RTL'
import { SplashScreen } from './components/SplashScreen'
import { MaintenanceScreen } from './components/MaintenanceScreen'
import { Toaster } from './components/Toaster'
import { SettingsProvider } from './contexts/settings-context'
import {
  SessionQueryClientProvider,
  sessionKeyOf,
} from './contexts/session-query-client'
import type { AuthContextType } from './contexts/auth/jwt-context'
import { AuthConsumer, AuthProvider } from './contexts/auth/jwt-context'
import { useNprogress } from './hooks/use-nprogress'
import { useSettings } from './hooks/use-settings'
import { routes } from './routes'
import { store } from './store'
import { createTheme } from './theme'
import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query'
import type { Query, Mutation } from '@tanstack/react-query'
import * as Sentry from '@sentry/browser'
import { reportErrorBoundaryToSentry } from '@/lib/sentry-report-boundary'
import { Error500 } from '@/pages/Error500'
import { ConfirmContextProvider } from './components/ConfirmModal'
import { defaultConfig } from '@/config'
import IntercomWrapper from './components/IntercomWrapper'
import PostHogIdentityWrapper from './components/PostHogIdentityWrapper'
import { NewVersionNotifier } from './components/NewVersionNotifier'
import { DocumentTitle } from './components/DocumentTitle'
import type { PaletteMode } from '@/models'

import './locales/i18n'
import { useNetworkStatus } from './hooks/use-network-status'
import UserUiPreferencesSync from './components/UserUiPreferencesSync'

function sentryShortKey(key: unknown): string | undefined {
  try {
    const s = JSON.stringify(key)
    return s.length > 500 ? `${s.slice(0, 500)}…` : s
  } catch {
    return undefined
  }
}

function reactQuerySentryOnError(error: unknown, query: Query): void {
  if (defaultConfig().envMode !== 'production') return
  Sentry.addBreadcrumb({
    category: 'react-query',
    level: 'error',
    message: 'Query error',
    data: {
      queryKey: sentryShortKey(query.queryKey),
    },
  })
}

function reactQueryMutationSentryOnError(
  error: unknown,
  mutation: Mutation<unknown, unknown, unknown, unknown>
): void {
  if (defaultConfig().envMode !== 'production') return
  Sentry.addBreadcrumb({
    category: 'react-query',
    level: 'error',
    message: 'Mutation error',
    data: {
      mutationKey: mutation.options.mutationKey
        ? sentryShortKey(mutation.options.mutationKey)
        : undefined,
    },
  })
}

const createQueryClient = (): QueryClient =>
  new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        retry: false,
        staleTime: 30000,
      },
    },
    queryCache: new QueryCache({
      onError: (error, query) => {
        console.error('Error: ', error)
        reactQuerySentryOnError(error, query)
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        console.error('Mutation error: ', error)
        reactQueryMutationSentryOnError(error, mutation)
      },
    }),
  })

interface AppThemeShellProps {
  auth: AuthContextType
  element: ReactNode
}

const AppThemeShell: FC<AppThemeShellProps> = (props) => {
  const { auth, element } = props
  const settings = useSettings()
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)', {
    noSsr: true,
  })

  const resolvedPaletteMode: PaletteMode =
    settings.paletteMode === 'dark'
      ? 'dark'
      : settings.paletteMode === 'auto'
      ? prefersDark
        ? 'dark'
        : 'light'
      : 'light'

  const theme = createTheme({
    contrast: settings.contrast,
    direction: settings.direction,
    paletteMode: resolvedPaletteMode,
    responsiveFontSizes: settings.responsiveFontSizes,
  })

  const showSplashScreen = !auth.isInitialized

  return (
    <SessionQueryClientProvider
      key={sessionKeyOf(auth)}
      createClient={createQueryClient}
    >
      <ThemeProvider theme={theme}>
        <Helmet>
          <meta name="color-scheme" content={resolvedPaletteMode} />
          <meta name="theme-color" content={theme.palette.neutral[900]} />
        </Helmet>
        <DocumentTitle />
        <RTL direction={settings.direction}>
          <CssBaseline />
          {auth.maintenance ? (
            // Hors du ConfirmContextProvider : une confirmation ouverte avant la coupure se ferme.
            <MaintenanceScreen until={auth.maintenance.until} />
          ) : (
            <ConfirmContextProvider>
              {showSplashScreen ? (
                <SplashScreen />
              ) : (
                <PostHogIdentityWrapper>
                  <IntercomWrapper>{element}</IntercomWrapper>
                </PostHogIdentityWrapper>
              )}
              <UserUiPreferencesSync />
              <NewVersionNotifier />
            </ConfirmContextProvider>
          )}
          {/* Toujours monté, pour que ses toasts échoient ; masqué pendant la coupure, où les appels en vol échouent. */}
          <Toaster hidden={Boolean(auth.maintenance)} />
        </RTL>
      </ThemeProvider>
    </SessionQueryClientProvider>
  )
}

export const App: FC = () => {
  useNprogress()
  useNetworkStatus()

  const element = useRoutes(routes)

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      void navigator.serviceWorker
        .register('/service-worker.js')
        .then((registration) => console.info('scope is: ', registration.scope))
    }
  }, [])

  return (
    <ErrorBoundary
      FallbackComponent={Error500}
      onError={reportErrorBoundaryToSentry}
    >
      <ReduxProvider store={store}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <AuthProvider>
            <AuthConsumer>
              {(auth) => (
                <SettingsProvider>
                  <AppThemeShell auth={auth} element={element} />
                </SettingsProvider>
              )}
            </AuthConsumer>
          </AuthProvider>
        </LocalizationProvider>
      </ReduxProvider>
    </ErrorBoundary>
  )
}
